// FileRendererDataProvider.jsx
// Responsible for data storage and provision of the FileRenderer component.

// Copyright HS Analysis GmbH, 2023
// Author: Jakob Burkhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// HSA Imports
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import Backend from "../../common/utils/Backend";
import FileViewerRenderer from "./FileViewerRenderer";
import LinearProgress from "@mui/material/LinearProgress";

/**
 * Element containing all visualisations of all audio files / streams in a project.
 */
function FileRendererDataProvider(props) {
  const FV = props.FV;
  const [timeLineHeight, setTimeLineHeight] = React.useState(170);
  const [showZStackBar, setShowZStackBar] = React.useState(false);
  const [displayZStackBar, setDisplayZStackBar] = React.useState(false);
  const [displayTimeBar, setDisplayTimeBar] = React.useState(false);
  const importPollRef = React.useRef(null);

  const showFullscreen = false;
  const zoomROI = false;
  const changingFile = false;

  // triggers on new Selected File
  React.useEffect(() => {
    const fileId = props.currentFileId;
    if (!fileId) return;

    // Helper to clear any running poller
    const stopPolling = () => {
      if (importPollRef.current) {
        clearInterval(importPollRef.current);
        importPollRef.current = null;
      }
    };

    // Proactively check if this file is currently being imported in the background
    const checkAndPollImport = async () => {
      try {
        const info = await Backend.getFileInfo(fileId);
        const progress = Number(info?.progress);
        if (!Number.isNaN(progress) && progress >= 0 && progress < 100) {
          // Import is ongoing; reflect progress and start polling
          FV.setImportProgress(progress);
          stopPolling();
          importPollRef.current = setInterval(async () => {
            try {
              const fi = await Backend.getFileInfo(fileId);
              const p = Number(fi?.progress);
              if (!Number.isNaN(p)) {
                // Only update UI with non-negative progress; treat -1 as "not started yet"
                if (p >= 0) {
                  FV.setImportProgress(p);
                }
                if (p >= 100) {
                  // Finished: stop polling and try to load the image metadata
                  stopPolling();
                  // Give the backend a brief moment to finalize files
                  setTimeout(() => {
                    let loaded = false;
                    const fallback = setTimeout(() => {
                      if (!loaded) {
                        // As a robust fallback, reload the page like the legacy viewer
                        window.location.reload();
                      }
                    }, 1500);

                    Backend.loadImage(
                      { id: fileId },
                      (ome) => {
                        if (!ome?.fileName) return;
                        loaded = true;
                        clearTimeout(fallback);
                        setShowZStackBar(ome.sizeZ > 1);
                        setDisplayZStackBar(ome.sizeT > 1);
                        setDisplayTimeBar(ome.sizeT > 1);
                        FV.setImportProgress(-1);
                        FV.setOmeDict((prev) => ({ ...prev, [fileId]: ome }));
                        FV.setHistogramConfigs((prev) => ({
                          ...prev,
                          [fileId]: getHistogramFromOme(ome),
                        }));
                      },
                      (error) => {
                        console.log("error import (post-finish load)", error);
                      },
                    );
                  }, 500);
                }
              }
            } catch (err) {
              console.warn("Polling getFileInfo failed", err);
            }
          }, 1000);
        } else if (progress === 100) {
          // Already imported; clear any residual progress
          FV.setImportProgress(-1);
        } else if (progress === -1) {
          // Import has not started yet; begin polling to catch when it switches to >= 0
          stopPolling();
          importPollRef.current = setInterval(async () => {
            try {
              const fi = await Backend.getFileInfo(fileId);
              const p = Number(fi?.progress);
              if (!Number.isNaN(p)) {
                if (p >= 0) {
                  FV.setImportProgress(p);
                }
                if (p >= 100) {
                  stopPolling();
                  setTimeout(() => {
                    let loaded = false;
                    const fallback = setTimeout(() => {
                      if (!loaded) {
                        window.location.reload();
                      }
                    }, 1500);

                    Backend.loadImage(
                      { id: fileId },
                      (ome) => {
                        if (!ome?.fileName) return;
                        loaded = true;
                        clearTimeout(fallback);
                        setShowZStackBar(ome.sizeZ > 1);
                        setDisplayZStackBar(ome.sizeT > 1);
                        setDisplayTimeBar(ome.sizeT > 1);
                        FV.setImportProgress(-1);
                        FV.setOmeDict((prev) => ({ ...prev, [fileId]: ome }));
                        FV.setHistogramConfigs((prev) => ({
                          ...prev,
                          [fileId]: getHistogramFromOme(ome),
                        }));
                      },
                      (error) => {
                        console.log("error import (post-finish load)", error);
                      },
                    );
                  }, 500);
                }
              }
            } catch (err) {
              console.warn("Polling getFileInfo failed", err);
            }
          }, 1000);
        } else {
          // Unknown state; do not force-hide progress
        }
      } catch (err) {
        // If the info fetch fails, just proceed with normal load and let its progress callback drive UI
        console.warn("getFileInfo failed", err);
      }
    };

    checkAndPollImport();

    // Always attempt to load image (this also triggers import and progress callbacks when applicable)
    Backend.loadImage(
      { id: fileId },
      (ome) => {
        if (!ome?.fileName) return;
        setShowZStackBar(ome.sizeZ > 1);
        setDisplayZStackBar(ome.sizeT > 1);
        setDisplayTimeBar(ome.sizeT > 1);
        FV.setImportProgress(-1);

        FV.setOmeDict((prevOmeDict) => ({
          ...prevOmeDict,
          [fileId]: ome,
        }));

        FV.setHistogramConfigs((prevConfigs) => ({
          ...prevConfigs,
          [fileId]: getHistogramFromOme(ome),
        }));
      },
      (error) => {
        console.log("error import", error);
        // If loadImage fails while import is ongoing, make sure we still show a visible progress overlay
        // until polling via getFileInfo provides a proper value.
        if (typeof FV.importProgress !== "number" || FV.importProgress < 0) {
          FV.setImportProgress(0);
        }
      },
      (e) => {
        // Progress callback from loadImage (if available)
        if (typeof e?.progress === "number") {
          console.log("Import progress:", e);
          FV.setImportProgress(e.progress);
        }
      },
    );

    return () => {
      // Cleanup OME and histogram when leaving the file
      FV.setOmeDict((prevOmeDict) => {
        const rest = { ...prevOmeDict };
        delete rest[fileId];
        return rest;
      });

      FV.setHistogramConfigs((prevConfigs) => {
        const rest = { ...prevConfigs };
        delete rest[fileId];
        return rest;
      });

      // Stop any running import polling
      stopPolling();
    };
  }, [props.currentFileId]);

  const resizeTimeLine = (deltaY, resizeEnd) => {
    let newTimeLineHeight = timeLineHeight + deltaY;
    if (
      newTimeLineHeight > 90 &&
      window.innerHeight - newTimeLineHeight - 64 > 100
    ) {
      setTimeLineHeight(newTimeLineHeight);
      props.persistentStorage.save("timeLineHeight", newTimeLineHeight);
    }
    if (resizeEnd) {
      props.updateDimensions();
    }
  };

  const resetRoiZoomInDict = (fileId) => {
    let newZoomObjectDict = FV.zoomObjectDict;
    if (newZoomObjectDict) {
      newZoomObjectDict[fileId].zoomRoi = false;
      FV.setZoomObjectDict((prevState) => ({
        ...prevState,
        ...newZoomObjectDict,
      }));
    }
  };

  const getHistogramFromOme = (ome) => {
    let channels = [];
    for (let c of ome.channels) {
      let color = c.color.includes("#")
        ? c.color
        : "#" + parseInt(c.color).toString(16).padStart(6, "0");
      channels.push({
        color: color, //convert decimal color to hex color
        enabled: true,
        gamma: c.gamma,
        max: 250,
        min: 0,
        name: c.name,
        type: c.type,
      });
    }

    let histogramConfig = {
      channels: channels,
      nChannels: ome.channels.length,
    };

    return histogramConfig;
  };

  return (
    <div style={{ height: "100%", position: "relative" }}>
      {FV.histogramConfigs[props.currentFileId] &&
      FV.omeDict &&
      FV.omeDict[props.currentFileId] &&
      FV.activeFileId ? (
        <FileViewerRenderer
          FV={FV}
          key={FV.containerKey}
          activeFileId={FV.activeFileId}
          canvasId={"caseViewerCanvas" + props.index}
          chainListFileIds={props.chainListFileIds}
          changingFile={changingFile}
          componentRef={(c) =>
            FV.setRendererDict({
              ...FV.rendererDict,
              [props.currentFileId]: c,
            })
          }
          displayTimeBar={displayTimeBar}
          displayZStackBar={displayZStackBar}
          selectedLayer={0}
          structures={[]}
          frameArrayDict={{}}
          resizeTimeLine={resizeTimeLine}
          timeLineHeight={timeLineHeight}
          fileId={props.currentFileId}
          histogramConfig={FV.histogramConfigs[props.currentFileId]}
          isActive={FV.activeFileId === props.currentFileId}
          isChained={FV.chainListFileIds.includes(props.currentFileId)}
          ome={FV.omeDict[props.currentFileId]}
          onChangeChain={() => {
            console.log("change chain");
          }}
          rendererDict={FV.rendererDict}
          rightSpace={50 + FV.previewWidth + FV.treeWidth}
          showFullscreen={showFullscreen}
          showZStackBar={showZStackBar}
          splitscreenCount={FV.splitscreenFileIds.length}
          splitscreenFileIds={FV.splitscreenFileIds}
          splitscreenIdx={props.index}
          tZoomROI1={() => {
            resetRoiZoomInDict();
          }}
          updateGlobalT={() => {
            return null;
          }}
          updateGlobalZ={() => {
            return null;
          }}
          zoomBottom={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomBottom
              : 0
          }
          zoomLeft={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomLeft
              : 0
          }
          zoomScale={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomScale
              : undefined
          }
          zoomCenterX={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomCenterX
              : undefined
          }
          zoomCenterY={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomCenterY
              : undefined
          }
          zoomROI={zoomROI}
          zoomRight={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomRight
              : 0
          }
          zoomTop={
            FV.zoomObjectDict[props.currentFileId]
              ? FV.zoomObjectDict[props.currentFileId].zoomTop
              : 0
          }
          getMousePosition={props.getMousePosition}
          openNextFile={props.openNextFile}
          openPrevFile={props.openPrevFile}
          updateDimensions={props.updateDimensions}
        />
      ) : (
        FV.activeFileId === props.currentFileId &&
        !FV.processingRotation &&
        typeof FV.importProgress === "number" &&
        FV.importProgress >= 0 &&
        FV.importProgress <= 100 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                minWidth: 400,
                padding: 6,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  margin: "8px 8px 0 8px",
                  color: "#333",
                  textAlign: "center",
                }}
              >
                Importing file:
                <br />
                {FV.activeFileName || "Current file"}
              </div>
              <div style={{ position: "relative", width: 400, padding: 6 }}>
                <LinearProgress
                  variant="determinate"
                  value={FV.importProgress}
                  sx={{
                    height: 32,
                    borderRadius: "4px !important",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: "4px !important",
                    },
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#FFF",
                    fontWeight: 700,
                    fontSize: 14,
                    pointerEvents: "none",
                    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  }}
                >
                  {Number(FV.importProgress || 0)}%
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}

FileRendererDataProvider.propTypes = {
  persistentStorage: PropTypes.object,
  updateDimensions: PropTypes.func,
  currentFileId: PropTypes.string,
  index: PropTypes.number,
  getMousePosition: PropTypes.func,
  openNextFile: PropTypes.func,
  openPrevFile: PropTypes.func,
  chainListFileIds: PropTypes.array,
  histogramConfigs: PropTypes.object, // Already present, but FV is the prop, not histogramConfigs directly
  FV: PropTypes.shape({
    histogramConfigs: PropTypes.object,
    omeDict: PropTypes.object,
    activeFileId: PropTypes.string,
    activeFileName: PropTypes.string,
    importProgress: PropTypes.number,
    processingRotation: PropTypes.bool,
    setImportProgress: PropTypes.func,
    setOmeDict: PropTypes.func,
    setHistogramConfigs: PropTypes.func,
    containerKey: PropTypes.any,
    rendererDict: PropTypes.object,
    setRendererDict: PropTypes.func,
    previewWidth: PropTypes.number,
    treeWidth: PropTypes.number,
    splitscreenFileIds: PropTypes.array,
    chainListFileIds: PropTypes.array,
    zoomObjectDict: PropTypes.object,
    setZoomObjectDict: PropTypes.func,
  }),
};

export default withPersistentStorage(FileRendererDataProvider);
